import React from "react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { PreviousResultsTable } from "../components/PreviousResultsTable";
import { InternalLinks } from "../components/InternalLinks";
import { SEOContentSection } from "../components/SEOContentSection";
import { useSEO } from "../hooks/useSEO";
import { SITE_URL } from "../lib/api";
import { relatedFor } from "../data/pages";

export default function PreviousResultPage({ config }) {
  const { result, common } = relatedFor(config.variant);

  useSEO({
    title: config.title,
    description: config.description,
    path: config.slug,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { name: "Home", item: SITE_URL + "/" },
          { name: config.kicker, item: SITE_URL + config.slug },
          { name: config.h1, item: SITE_URL + config.slug },
        ].map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.item })),
      },
    ],
  });

  const content = [
    { h: config.h1, p: config.intro },
    {
      h: "Understanding the table",
      p: "Each row lists the date, city, First Round (F/R) and Second Round (S/R) numbers. The table is fully responsive and scrolls cleanly on small phones, with pagination to browse older records without slowing the page.",
    },
    {
      h: "How previous results stay updated",
      p: "There is a single source of truth for result data. When a new result is published, the earlier result is automatically moved into this archive — so you never see the same number entered twice, and history is always complete.",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs items={[{ label: config.kicker }, { label: config.h1 }]} />
      <h1 className="font-head font-black text-3xl sm:text-4xl text-slate-900 mb-2">{config.h1}</h1>
      <p className="text-slate-600 max-w-3xl mb-8 text-[15px] leading-relaxed">{config.intro}</p>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-10">
          <PreviousResultsTable variant={config.variant} cityLabel={config.h1} />
          <SEOContentSection blocks={content} />
        </div>
        <div className="lg:col-span-1 space-y-6">
          <InternalLinks
            title="Related pages"
            links={[
              { label: `${result.h1}`, to: result.slug },
              { label: `${common.h1}`, to: common.slug },
              { label: "Home — Live Result", to: "/" },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
